<template>
  <div class="formulario-modelo">

    <!-- ══ Encabezado ══ -->
    <div class="pg-header">
      <h1>Estructura Organizacional</h1>
      <p class="pg-sub">Cada fila representa un nivel organizacional. Haz clic en un área para ver su ruta y gestionarla.</p>
    </div>
    <div class="pg-divider"></div>

    <!-- ══ Campus (oculto en modo foco) ══ -->
    <div v-if="!cardSeleccionada" class="campus-seccion">
      <div class="campus-cabecera" @click="mostrarGestionCampus = !mostrarGestionCampus">
        <span class="campus-tit">Campus</span>
        <div class="campus-pills-preview">
          <button
            v-for="c in campusStore.campus"
            :key="c.id_campus"
            :class="['c-pill-preview', { 'c-pill-activa': campusFiltro === c.id_campus }]"
            @click.stop="toggleFiltroCampus(c.id_campus)"
          >{{ c.nombre }}</button>
        </div>
        <span class="campus-caret">{{ mostrarGestionCampus ? '▴' : '▾' }}</span>
      </div>

      <div v-if="mostrarGestionCampus" class="campus-cuerpo">
        <div class="campus-chips">
          <button
            v-for="c in campusStore.campus"
            :key="c.id_campus"
            :class="['campus-chip', { 'campus-chip-activo': campusSeleccionado?.id_campus === c.id_campus }]"
            @click="seleccionarCampus(c)"
          >{{ c.nombre }}</button>
          <button class="campus-chip campus-chip-nuevo" @click="abrirCrearCampus">+ Nuevo campus</button>
        </div>

        <div v-if="modoCampus" class="campus-form">
          <div class="campus-form-campos">
            <input
              v-model="formCampus.nombre"
              type="text"
              placeholder="Nombre del campus"
              class="input-campus"
              @keyup.enter="guardarCampus"
            />
            <select v-model="formCampus.estado" class="input-campus-select">
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </select>
          </div>
          <div class="campus-form-acciones">
            <button
              v-if="modoCampus === 'editar'"
              class="btn salir"
              :disabled="procesando"
              @click="confirmarEliminarCampus(campusSeleccionado!)"
            >Eliminar</button>
            <button class="btn salir" @click="cerrarFormCampus">Cancelar</button>
            <button class="btn guardar" :disabled="guardandoCampus" @click="guardarCampus">
              {{ guardandoCampus ? 'Guardando...' : (modoCampus === 'editar' ? 'Guardar cambios' : 'Crear campus') }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- ══ Cargando ══ -->
    <div v-if="cargando" class="cargando-est">
      Cargando estructura...
    </div>

    <!-- ══ Árbol organizacional ══ -->
    <div v-else class="org-wrap">

      <!-- Barra de foco: aparece al seleccionar un área -->
      <div v-if="cardSeleccionada" class="foco-bar">
        <button
          class="btn-volver"
          @click="cardSeleccionada = null; cardSeleccionadaFilaIdx = -1"
        >← Volver</button>
        <div class="foco-info">
          <span class="foco-nombre">{{ cardSeleccionada.nombre }}</span>
          <span v-if="padreDeSeleccionada" class="foco-dep">depende de <strong>{{ padreDeSeleccionada.nombre }}</strong></span>
          <span v-else class="foco-dep">Área raíz</span>
        </div>
        <div class="foco-acciones">
          <button class="btn guardar" :disabled="procesando" @click="abrirEditar(cardSeleccionada!)">Modificar</button>
          <button class="btn salir-foco" :disabled="procesando" @click="confirmarEliminar(cardSeleccionada!); cardSeleccionada = null">Eliminar</button>
        </div>
      </div>

      <!-- Contenedor scrollable / pannable -->
      <div
        class="matriz-contenedor"
        ref="contenedorRef"
        :class="{ 'is-panning': panState.active }"
        @mousedown="onPanStart"
      >
        <div class="matriz" ref="matrizRef">

          <!-- Separador inicial (oculto en modo foco) -->
          <div v-if="!cardSeleccionada" class="separador-fila">
            <button class="btn-insertar" @click="insertarFilaEn(0)">+ Insertar fila</button>
          </div>

          <template v-for="(fila, i) in filasDisplay" :key="fila.tipo === 'vacia' ? fila.id : `real-${i}`">

            <!-- En modo foco: solo mostrar filas con áreas en la ruta -->
            <template
              v-if="!cardSeleccionada || (
                fila.tipo === 'real' &&
                fila.areas.some(a => idsResaltados.has(a.id_programa!))
              )"
            >

              <!-- Fila de nivel -->
              <div
                class="nivel-fila"
                :class="{ 'nivel-vacia': fila.tipo === 'vacia' || !fila.areas.length }"
              >
                <div class="nivel-badge">{{ i + 1 }}</div>

                <div v-if="fila.tipo === 'vacia' || !fila.areas.length" class="nivel-vacia-body">
                  <button
                    v-if="!cardSeleccionada"
                    class="btn-agregar-vacio"
                    @click="abrirCrearEnNivel(i)"
                  >
                    + Agregar área a esta fila
                  </button>
                </div>

                <div v-else class="nivel-areas">
                  <AreaCard
                    v-for="area in (cardSeleccionada
                      ? fila.areas.filter(a => idsResaltados.has(a.id_programa!))
                      : fila.areas)"
                    :key="area.id_programa"
                    :area="area"
                    :nivel="i"
                    :seleccionada="cardSeleccionada?.id_programa === area.id_programa"
                    :en-ruta="idsResaltados.size > 0 && idsResaltados.has(area.id_programa!) && cardSeleccionada?.id_programa !== area.id_programa"
                    :opaca="idsResaltados.size > 0 && !idsResaltados.has(area.id_programa!)"
                    @seleccionar="seleccionarCard($event, i)"
                    @reordenar="onReordenar"
                  />
                  <button
                    v-if="!cardSeleccionada"
                    class="btn-nuevo-area"
                    @click="abrirCrearEnNivel(i)"
                    title="Agregar área en esta fila"
                  >+</button>
                </div>
              </div>

              <!-- Panel de detalle (paneles SNIES / Institución / Directivos) -->
              <div v-if="cardSeleccionada && cardSeleccionadaFilaIdx === i" class="panel-detalle">
                <RegistrosSniesPanel
                  v-if="cardSeleccionada.tipo === 'programa'"
                  :id-programa="cardSeleccionada.id_programa!"
                />
                <InstitucionPanel v-if="cardSeleccionada.tipo === 'institucion'" />
                <DirectivosPanel
                  v-if="cardSeleccionada.tipo !== 'programa'"
                  :id-area="cardSeleccionada.id_programa!"
                />
              </div>

              <!-- Separador entre filas (oculto en modo foco) -->
              <div v-if="!cardSeleccionada" class="separador-fila">
                <button class="btn-insertar" @click="insertarFilaEn(i + 1)">+ Insertar fila</button>
              </div>

            </template>

          </template>

          <!-- Agregar fila al final (oculto en modo foco) -->
          <button v-if="!cardSeleccionada" class="btn-agregar-fila" @click="agregarFila">
            + Agregar fila
          </button>

        </div>
      </div>
    </div>

    <!-- ══ Modal crear / editar área ══ -->
    <AreaModal
      ref="modalRef"
      :visible="modalVisible"
      :area="areaSeleccionada"
      :opciones-planas="opcionesParaModal"
      :opciones-campus="campusStore.campus"
      @cancelar="cerrarModal"
      @guardar="guardarArea"
    />

    <MensajeConfirmacion
      :visible="!!campusParaEliminar"
      title="Eliminar campus"
      :message="`¿Desea eliminar el campus &quot;${campusParaEliminar?.nombre ?? ''}&quot;? Esta acción fallará si tiene áreas o procesos asociados.`"
      confirmText="Eliminar"
      cancelText="Cancelar"
      :confirm-loading="procesando"
      @confirm="ejecutarEliminarCampus"
      @cancel="campusParaEliminar = null"
    />

    <MensajeConfirmacion
      :visible="!!areaParaEliminar"
      title="Eliminar área"
      :message="`¿Desea eliminar &quot;${areaParaEliminar?.nombre ?? ''}&quot;? Esta acción fallará si tiene sub-áreas o procesos asociados.`"
      confirmText="Eliminar"
      cancelText="Cancelar"
      :confirm-loading="procesando"
      @confirm="ejecutarEliminar"
      @cancel="areaParaEliminar = null"
    />

    <MensajeFlotante
      :visible="mensajeVisible"
      :mensaje="mensajeTexto"
      :tipo="mensajeTipo"
      @cerrar="mensajeVisible = false"
    />

    <!-- ══ Modal reasignación de padre en fila intermedia ══ -->
    <div v-if="modalReasignarVisible" class="reasignar-overlay">
      <div class="reasignar-panel">
        <h3>Reasignar dependencias</h3>
        <p>
          <strong>{{ nuevaAreaReasignar?.nombre }}</strong> fue insertada en una fila intermedia.
          Selecciona cuáles áreas de la fila de abajo deberían depender ahora de ella:
        </p>
        <div class="reasignar-lista">
          <label
            v-for="a in candidatasReasignar"
            :key="a.id_programa"
            class="reasignar-item"
          >
            <input type="checkbox" :value="a.id_programa" v-model="seleccionadasArr" />
            <span>{{ a.nombre }}</span>
          </label>
        </div>
        <div class="reasignar-acciones">
          <button class="btn salir" :disabled="procesando" @click="cancelarReasignar">Omitir</button>
          <button
            class="btn guardar"
            :disabled="procesando || !seleccionadasArr.length"
            @click="confirmarReasignar"
          >
            {{ procesando ? 'Guardando...' : 'Confirmar' }}
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script lang="ts" setup>
/**
 * Árbol organizacional de áreas: sedes, facultades y programas.
 *
 * Sin límite de profundidad, a diferencia del árbol de componentes. Es un bosque:
 * admite varias raíces, una por sede.
 *
 * ── Enrutado ─────────────────────────────────────────────────────
 *   `/gestion-areas` (GestionAreas) — permiso `campus.ver`
 *
 * Vista enrutada. Obtiene sus datos a través de stores Pinia: no llama a `http`
 * ni a los servicios directamente.
 */

import { ref, computed, onMounted, onUnmounted, provide, reactive } from 'vue'
import { useGestionProgramas } from '@/store/GestionPrograma'
import { useGestionCampus } from '@/store/GestionCampus'
import type { Programa } from '@/interfaces/Programa'
import type { Campus } from '@/interfaces/Campus'
import AreaModal from '@/components/AreaModal.vue'
import AreaCard from '@/components/AreaCard.vue'
import RegistrosSniesPanel from '@/components/RegistrosSniesPanel.vue'
import InstitucionPanel from '@/components/InstitucionPanel.vue'
import DirectivosPanel from '@/components/DirectivosPanel.vue'
import MensajeFlotante from '@/components/MensajeFlotante.vue'
import MensajeConfirmacion from '@/components/MensajeConfirmacion.vue'

type FilaDisplay =
  | { tipo: 'real'; areas: Programa[] }
  | { tipo: 'vacia'; id: string }

const store       = useGestionProgramas()
const campusStore = useGestionCampus()

const cargando        = ref(false)
const procesando      = ref(false)
const mostrarGestionCampus  = ref(false)
const campusSeleccionado    = ref<Campus | null>(null)
const campusFiltro          = ref<number | null>(null)
const modoCampus            = ref<'crear' | 'editar' | null>(null)
const guardandoCampus       = ref(false)
const campusParaEliminar    = ref<Campus | null>(null)
const formCampus = reactive({ nombre: '', estado: 'activo' as 'activo' | 'inactivo' })
const modalVisible    = ref(false)
const areaSeleccionada  = ref<Programa | null>(null)
const areaParaEliminar  = ref<Programa | null>(null)
const modalRef          = ref<InstanceType<typeof AreaModal> | null>(null)
const filasManual       = ref<FilaDisplay[] | null>(null)
const cardSeleccionada      = ref<Programa | null>(null)
const cardSeleccionadaFilaIdx = ref(-1)
const filaDeCreacion        = ref(-1)
const modalReasignarVisible = ref(false)
const candidatasReasignar   = ref<Programa[]>([])
const seleccionadasArr      = ref<number[]>([])
const nuevaAreaReasignar    = ref<Programa | null>(null)
const mensajeVisible = ref(false)
const mensajeTexto   = ref('')
const mensajeTipo    = ref<'positivo' | 'error'>('positivo')

const contenedorRef = ref<HTMLElement | null>(null)
const panState = reactive({ active: false, startX: 0, startY: 0, scrollLeft: 0 })

function onPanStart(e: MouseEvent) {
  if ((e.target as HTMLElement).closest('button, input, select, label, a')) return
  panState.active     = true
  panState.startX     = e.clientX
  panState.scrollLeft = contenedorRef.value?.scrollLeft ?? 0
}

function onPanMove(e: MouseEvent) {
  if (!panState.active || !contenedorRef.value) return
  contenedorRef.value.scrollLeft = panState.scrollLeft - (e.clientX - panState.startX)
}

function onPanEnd() {
  panState.active = false
}

const arbol = computed(() => store.arbol)

const dragState = reactive<{
  dragging: Programa | null
  dropTargetId: number | null
  dropSide: 'before' | 'after'
}>({ dragging: null, dropTargetId: null, dropSide: 'after' })
provide('areaDragState', dragState)

// Filas display
function calcularNiveles(nodos: Programa[], prof = 0, acum: Programa[][] = []): Programa[][] {
  if (!nodos.length) return acum
  if (!acum[prof]) acum[prof] = []
  for (const n of nodos) {
    acum[prof].push(n)
    if (n.hijos_recursivos?.length) calcularNiveles(n.hijos_recursivos, prof + 1, acum)
  }
  return acum
}

const filasFromArbol = computed<FilaDisplay[]>(() =>
  calcularNiveles(arbol.value).map(areas => ({ tipo: 'real' as const, areas }))
)

const filasDisplay = computed<FilaDisplay[]>(() =>
  filasManual.value ?? filasFromArbol.value
)

// Opciones del modal
function aplanarExcluyendo(nodos: Programa[], excluirId: number | null, depth = 0): { id: number; label: string; nombre: string }[] {
  const result: { id: number; label: string; nombre: string }[] = []
  for (const n of nodos) {
    if (n.id_programa === excluirId) continue
    const indent = depth > 0 ? '  '.repeat(depth) + '– ' : ''
    result.push({ id: n.id_programa!, label: indent + n.nombre, nombre: n.nombre })
    if (n.hijos_recursivos?.length)
      result.push(...aplanarExcluyendo(n.hijos_recursivos, excluirId, depth + 1))
  }
  return result
}

const opcionesParaModal = computed(() =>
  aplanarExcluyendo(arbol.value, areaSeleccionada.value?.id_programa ?? null)
)

// Card seleccionada
function buscarEnArbol(nodos: Programa[], id: number): Programa | null {
  for (const n of nodos) {
    if (n.id_programa === id) return n
    const found = buscarEnArbol(n.hijos_recursivos ?? [], id)
    if (found) return found
  }
  return null
}

const padreDeSeleccionada = computed(() => {
  if (!cardSeleccionada.value?.padre_id) return null
  return buscarEnArbol(arbol.value, cardSeleccionada.value.padre_id)
})

function aplanarMapa(nodos: Programa[], acum: Map<number, Programa> = new Map()): Map<number, Programa> {
  for (const n of nodos) {
    if (n.id_programa) acum.set(n.id_programa, n)
    if (n.hijos_recursivos?.length) aplanarMapa(n.hijos_recursivos, acum)
  }
  return acum
}

const mapaAreas = computed(() => aplanarMapa(arbol.value))

const idsResaltados = computed<Set<number>>(() => {
  if (!cardSeleccionada.value?.id_programa) return new Set()
  const ids = new Set<number>()
  let actual: Programa | undefined = cardSeleccionada.value
  while (actual?.id_programa) {
    ids.add(actual.id_programa)
    actual = actual.padre_id ? mapaAreas.value.get(actual.padre_id) : undefined
  }
  return ids
})

function seleccionarCard(area: Programa, filaIdx: number) {
  if (cardSeleccionada.value?.id_programa === area.id_programa) {
    cardSeleccionada.value = null
    cardSeleccionadaFilaIdx.value = -1
  } else {
    cardSeleccionada.value = area
    cardSeleccionadaFilaIdx.value = filaIdx
  }
}

function mostrarMensaje(texto: string, tipo: 'positivo' | 'error') {
  mensajeTexto.value = texto
  mensajeTipo.value  = tipo
  mensajeVisible.value = true
}

// Carga
async function cargar(idCampus?: number | null) {
  cargando.value = true
  try {
    await store.cargarArbol(idCampus)
    filasManual.value = null
    cardSeleccionada.value = null
    cardSeleccionadaFilaIdx.value = -1
  } finally {
    cargando.value = false
  }
}

function toggleFiltroCampus(idCampus: number) {
  campusFiltro.value = campusFiltro.value === idCampus ? null : idCampus
  cargar(campusFiltro.value)
}

// Insertar / agregar filas
function insertarFilaEn(index: number) {
  const base = [...(filasManual.value ?? filasFromArbol.value)]
  base.splice(index, 0, { tipo: 'vacia', id: Math.random().toString(36).slice(2) })
  filasManual.value = base
}

function agregarFila() {
  const base = [...(filasManual.value ?? filasFromArbol.value)]
  filasManual.value = [...base, { tipo: 'vacia', id: Math.random().toString(36).slice(2) }]
}

// Abrir modal
function abrirCrearEnNivel(filaIndex: number) {
  filaDeCreacion.value = filaIndex
  areaSeleccionada.value = { nombre: '' }
  modalVisible.value = true
}

async function abrirEditar(area: Programa) {
  cardSeleccionada.value = null
  // Cargar área fresca por ID para obtener el campus array actualizado
  const fresca = area.id_programa ? await store.cargarPorId(area.id_programa) : null
  areaSeleccionada.value = fresca ? { ...fresca } : { ...area }
  modalVisible.value = true
}

function cerrarModal() {
  modalVisible.value = false
  areaSeleccionada.value = null
}

// Guardar
async function guardarArea(payload: {
  nombre: string
  tipo?: string
  nivel?: 'pregrado' | 'posgrado' | null
  padre_id: number | null
  facultad?: string | null
  campus_ids: number[]
}) {
  modalRef.value?.setGuardando(true)
  modalRef.value?.setError('')

  if (areaSeleccionada.value?.id_programa) {
    const idArea = areaSeleccionada.value.id_programa
    const result = await store.actualizar(idArea, {
      nombre:   payload.nombre,
      tipo:     payload.tipo,
      nivel:    payload.nivel    ?? undefined,
      padre_id: payload.padre_id ?? undefined,
      facultad: payload.facultad ?? undefined
    })
    if (!result.ok) {
      const msg = Object.values(result.errors ?? {}).flat().join(' ') || 'No se pudo actualizar.'
      modalRef.value?.setError(msg)
      modalRef.value?.setGuardando(false)
      return
    }

    // Sincronizar campus: añadir los nuevos, quitar los removidos
    const campusActuales = (areaSeleccionada.value.campus ?? []).map(c => c.id_campus)
    const agregar = payload.campus_ids.filter(id => !campusActuales.includes(id))
    const quitar  = campusActuales.filter(id => !payload.campus_ids.includes(id))
    await Promise.all([
      ...agregar.map(id => store.asignarCampus(idArea, id)),
      ...quitar.map(id  => store.desasignarCampus(idArea, id))
    ])

    cerrarModal()
    mostrarMensaje('Área actualizada correctamente.', 'positivo')
    await cargar()
  } else {
    const nombre  = payload.nombre
    const padreId = payload.padre_id
    const result  = await store.crear({
      nombre,
      tipo:     payload.tipo,
      nivel:    payload.nivel    ?? undefined,
      padre_id: padreId         ?? undefined,
      facultad: payload.facultad ?? undefined
    })
    if (!result.ok) {
      const msg = Object.values(result.errors ?? {}).flat().join(' ') || 'No se pudo crear.'
      modalRef.value?.setError(msg)
      modalRef.value?.setGuardando(false)
      return
    }

    // Asignar campus seleccionados al área recién creada
    const idNueva = result.data?.id_programa
    if (idNueva && payload.campus_ids.length) {
      await Promise.all(payload.campus_ids.map(id => store.asignarCampus(idNueva, id)))
    }

    // Detectar si fue creada en fila intermedia (vacia)
    const idx = filaDeCreacion.value
    const esIntermedia = idx >= 0 && filasDisplay.value[idx]?.tipo === 'vacia'
    filaDeCreacion.value = -1

    cerrarModal()
    mostrarMensaje('Área creada correctamente.', 'positivo')
    await cargar()

    // Seleccionar automáticamente el área recién creada
    for (let i = 0; i < filasDisplay.value.length; i++) {
      const fila = filasDisplay.value[i]
      if (fila.tipo !== 'real') continue
      const encontrada = fila.areas.find(
        a => a.nombre === nombre && (a.padre_id ?? null) === padreId
      )
      if (encontrada) {
        cardSeleccionada.value = encontrada
        cardSeleccionadaFilaIdx.value = i
        break
      }
    }

    // Si fue creada en fila intermedia y tiene padre, los demás hijos del padre son candidatos
    if (esIntermedia && idNueva && padreId !== null) {
      const padre = buscarEnArbol(arbol.value, padreId)
      const candidatas = (padre?.hijos_recursivos ?? []).filter(h => h.id_programa !== idNueva)
      if (candidatas.length > 0) {
        nuevaAreaReasignar.value = buscarEnArbol(arbol.value, idNueva)
        candidatasReasignar.value = candidatas
        seleccionadasArr.value = candidatas.map(a => a.id_programa!).filter(Boolean)
        modalReasignarVisible.value = true
      }
    }
  }
}

// Eliminar
function confirmarEliminar(area: Programa) {
  areaParaEliminar.value = area
}

async function ejecutarEliminar() {
  if (!areaParaEliminar.value?.id_programa || procesando.value) return
  procesando.value = true
  try {
    const result = await store.eliminar(areaParaEliminar.value.id_programa)
    areaParaEliminar.value = null
    if (result.ok) {
      mostrarMensaje('Área eliminada.', 'positivo')
      await cargar()
    } else {
      const msg = Object.values(result.errors ?? {}).flat().join(' ') || 'No se pudo eliminar.'
      mostrarMensaje(msg, 'error')
    }
  } finally {
    procesando.value = false
  }
}

// Reordenar
function encontrarHermanos(padreId: number | null | undefined, nodos: Programa[]): Programa[] {
  if (padreId == null) return nodos
  for (const n of nodos) {
    if (n.id_programa === padreId) return n.hijos_recursivos ?? []
    const found = encontrarHermanos(padreId, n.hijos_recursivos ?? [])
    if (found.length) return found
  }
  return []
}

// Campus CRUD
function abrirCrearCampus() {
  campusSeleccionado.value = null
  formCampus.nombre  = ''
  formCampus.estado  = 'activo'
  modoCampus.value   = 'crear'
}

function seleccionarCampus(campus: Campus) {
  if (campusSeleccionado.value?.id_campus === campus.id_campus) {
    campusSeleccionado.value = null
    modoCampus.value = null
    return
  }
  campusSeleccionado.value = campus
  formCampus.nombre  = campus.nombre
  formCampus.estado  = campus.estado ?? 'activo'
  modoCampus.value   = 'editar'
}

function cerrarFormCampus() {
  campusSeleccionado.value = null
  modoCampus.value  = null
  formCampus.nombre = ''
  formCampus.estado = 'activo'
}

async function guardarCampus() {
  if (!formCampus.nombre.trim()) return
  guardandoCampus.value = true
  try {
    if (modoCampus.value === 'editar' && campusSeleccionado.value) {
      const result = await campusStore.actualizar(campusSeleccionado.value.id_campus, {
        nombre: formCampus.nombre.trim(),
        estado: formCampus.estado
      })
      if (result.ok) {
        mostrarMensaje('Campus actualizado correctamente.', 'positivo')
        cerrarFormCampus()
      } else {
        const msg = Object.values(result.errors ?? {}).flat().join(' ') || 'No se pudo actualizar.'
        mostrarMensaje(msg, 'error')
      }
    } else {
      const result = await campusStore.crear({
        nombre: formCampus.nombre.trim(),
        estado: formCampus.estado
      })
      if (result.ok) {
        mostrarMensaje('Campus creado correctamente.', 'positivo')
        cerrarFormCampus()
      } else {
        const msg = Object.values(result.errors ?? {}).flat().join(' ') || 'No se pudo crear.'
        mostrarMensaje(msg, 'error')
      }
    }
  } finally {
    guardandoCampus.value = false
  }
}

function confirmarEliminarCampus(campus: Campus) {
  campusParaEliminar.value = campus
}

async function ejecutarEliminarCampus() {
  if (!campusParaEliminar.value) return
  procesando.value = true
  try {
    const result = await campusStore.eliminar(campusParaEliminar.value.id_campus)
    campusParaEliminar.value = null
    if (result.ok) {
      mostrarMensaje('Campus eliminado.', 'positivo')
      cerrarFormCampus()
    } else {
      const msg = Object.values(result.errors ?? {}).flat().join(' ') || 'No se pudo eliminar el campus.'
      mostrarMensaje(msg, 'error')
    }
  } finally {
    procesando.value = false
  }
}

async function onReordenar(payload: { dragged: Programa; target: Programa; side: 'before' | 'after' }) {
  const { dragged, target, side } = payload
  if (!dragged.id_programa || procesando.value) return
  procesando.value = true
  try {
    const hermanos   = encontrarHermanos(dragged.padre_id, arbol.value)
    const sinDragged = hermanos.filter(h => h.id_programa !== dragged.id_programa)
    const targetIdx  = sinDragged.findIndex(h => h.id_programa === target.id_programa)
    if (targetIdx === -1) return
    const nuevaPos = (side === 'before' ? targetIdx : targetIdx + 1) + 1
    const result = await store.reordenar(dragged.id_programa, nuevaPos)
    if (result.ok) await cargar()
    else mostrarMensaje(result.message ?? 'No se pudo reordenar.', 'error')
  } finally {
    procesando.value = false
  }
}

// Reasignación de padre
async function confirmarReasignar() {
  modalReasignarVisible.value = false
  const ids = [...seleccionadasArr.value]
  const nuevaId = nuevaAreaReasignar.value?.id_programa
  const snapshot = [...candidatasReasignar.value]
  nuevaAreaReasignar.value = null
  candidatasReasignar.value = []
  seleccionadasArr.value = []
  if (!ids.length || !nuevaId) return
  procesando.value = true
  try {
    await Promise.all(
      ids.map(id => {
        const area = snapshot.find(a => a.id_programa === id)
        if (!area) return Promise.resolve()
        return store.actualizar(id, {
          nombre:   area.nombre,
          tipo:     area.tipo,
          nivel:    area.nivel    ?? undefined,
          padre_id: nuevaId,
          facultad: (area as any).facultad ?? undefined
        })
      })
    )
    mostrarMensaje(`${ids.length} área(s) reasignadas correctamente.`, 'positivo')
    await cargar()
  } catch {
    mostrarMensaje('Error al reasignar áreas.', 'error')
  } finally {
    procesando.value = false
  }
}

function cancelarReasignar() {
  modalReasignarVisible.value = false
  nuevaAreaReasignar.value = null
  candidatasReasignar.value = []
  seleccionadasArr.value = []
}

onMounted(async () => {
  document.addEventListener('mousemove', onPanMove)
  document.addEventListener('mouseup', onPanEnd)
  await Promise.all([cargar(), campusStore.cargarTodos()])
})

onUnmounted(() => {
  document.removeEventListener('mousemove', onPanMove)
  document.removeEventListener('mouseup', onPanEnd)
})
</script>

<style scoped>
/* ══ Contenedor principal ══ */
.formulario-modelo {
  background-color: white;
  padding: 1.5rem 2rem;
  border-radius: var(--radius-lg);
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  box-shadow: var(--shadow-md);
  box-sizing: border-box;
  margin: 20px;
  font-family: var(--font-family);
}
@media (max-width: 640px) {
  .formulario-modelo { padding: 1rem; margin: 10px; }
}

/* ══ Encabezado de página ══ */
.pg-header { margin-bottom: 0; }
.pg-header h1 {
  font-size: 1.4rem;
  font-weight: 700;
  color: #111;
  margin: 0 0 0.2rem;
  letter-spacing: 0.01em;
}
.pg-sub {
  font-size: 0.84rem;
  color: #666;
  margin: 0;
}
.pg-divider {
  border: none;
  border-bottom: 1px solid #e8e8e8;
  margin: 1rem 0 1.25rem;
}

/* ══ Campus ══ */
.campus-seccion {
  margin-bottom: 1.5rem;
  border: 1px solid #d0e4f0;
  border-radius: 10px;
  overflow: hidden;
  background: #f5f9fd;
}
.campus-cabecera {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.7rem 1rem;
  cursor: pointer;
  user-select: none;
  transition: background 0.15s;
}
.campus-cabecera:hover { background: #ebf3fb; }
.campus-tit {
  font-size: 0.75rem;
  font-weight: 800;
  color: #0e69a0;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  white-space: nowrap;
  flex-shrink: 0;
}
.campus-pills-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  flex: 1;
  min-width: 0;
}
.c-pill-preview {
  font-size: 0.75rem;
  background: #dbeef9;
  color: #0e69a0;
  border-radius: 20px;
  padding: 2px 9px;
  font-weight: 500;
  border: 1px solid transparent;
  cursor: pointer;
  font-family: var(--font-family);
  transition: all 0.15s;
}
.c-pill-preview:hover { border-color: #eea222; }
.c-pill-activa { background: #eea222 !important; color: #111 !important; border-color: #eea222 !important; font-weight: 700; }
.campus-caret {
  font-size: 0.65rem;
  color: #0e69a0;
  flex-shrink: 0;
}
.campus-cuerpo {
  border-top: 1px solid #d0e4f0;
  padding: 0.85rem 1rem 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}
.campus-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 7px;
}
.campus-chip {
  padding: 5px 14px;
  border: 1px solid #b8d4e8;
  border-radius: 20px;
  background: white;
  font-size: 0.84rem;
  font-family: var(--font-family);
  font-weight: 500;
  color: #333;
  cursor: pointer;
  transition: all 0.15s;
}
.campus-chip:hover { border-color: #0e69a0; color: #0e69a0; }
.campus-chip-activo { background: #0e69a0; border-color: #0e69a0; color: white; }
.campus-chip-nuevo { border-style: dashed; color: #888; }
.campus-chip-nuevo:hover { border-color: #0e69a0; color: #0e69a0; }
.campus-form {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
  padding-top: 0.65rem;
  border-top: 1px solid #dde8f0;
}
.campus-form-campos {
  display: flex;
  gap: 0.75rem;
  flex-wrap: wrap;
}
.input-campus {
  flex: 1;
  min-width: 180px;
  padding: 0.5rem 0.75rem;
  border: 1px solid #dde4ec;
  border-radius: var(--radius-md, 6px);
  font-size: 0.875rem;
  font-family: var(--font-family);
  height: 36px;
  box-sizing: border-box;
  background: white;
}
.input-campus:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}
.input-campus-select {
  padding: 0.5rem 0.75rem;
  border: 1px solid #dde4ec;
  border-radius: var(--radius-md, 6px);
  font-size: 0.875rem;
  font-family: var(--font-family);
  height: 36px;
  cursor: pointer;
  background: white;
}
.input-campus-select:focus {
  outline: none;
  border-color: #0e69a0;
  box-shadow: 0 0 0 3px rgba(14, 105, 160, 0.1);
}
.campus-form-acciones {
  display: flex;
  gap: 8px;
  justify-content: flex-end;
}

/* ══ Estado de carga ══ */
.cargando-est {
  text-align: center;
  padding: 3rem 0;
  color: #999;
  font-size: 0.95rem;
}

/* ══ Árbol ══ */
.org-wrap {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

/* ── Barra de foco ── */
.foco-bar {
  position: sticky;
  top: 0;
  z-index: 10;
  display: flex;
  align-items: center;
  gap: 1rem;
  background: #0e69a0;
  border-radius: 8px;
  padding: 0.7rem 1rem;
  flex-wrap: wrap;
  box-shadow: 0 2px 8px rgba(14, 105, 160, 0.3);
}
.btn-volver {
  background: rgba(255, 255, 255, 0.15);
  border: 1px solid rgba(255, 255, 255, 0.4);
  color: white;
  border-radius: 6px;
  padding: 0.32rem 0.9rem;
  font-size: 0.84rem;
  font-weight: 600;
  cursor: pointer;
  font-family: var(--font-family);
  flex-shrink: 0;
  transition: background 0.15s;
}
.btn-volver:hover { background: rgba(255, 255, 255, 0.28); }
.foco-info {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  flex: 1;
  flex-wrap: wrap;
  min-width: 0;
}
.foco-nombre {
  font-weight: 700;
  font-size: 0.97rem;
  color: white;
}
.foco-dep {
  font-size: 0.8rem;
  color: rgba(255, 255, 255, 0.72);
}
.foco-dep strong { color: rgba(255, 255, 255, 0.9); }
.foco-acciones { display: flex; gap: 0.5rem; flex-shrink: 0; }
.foco-bar .btn.guardar {
  background: #eea222;
  color: #111;
  border: none;
  font-size: 0.82rem;
  height: 32px;
  padding: 0 14px;
}
.foco-bar .btn.guardar:hover { background: #fff; color: #0e69a0; }
.btn.salir-foco {
  padding: 0 14px;
  height: 32px;
  font-weight: 600;
  font-size: 0.82rem;
  font-family: var(--font-family);
  border-radius: var(--radius-md, 6px);
  cursor: pointer;
  transition: all 0.15s ease;
  background: rgba(255, 255, 255, 0.12);
  border: 1px solid rgba(255, 255, 255, 0.35);
  color: white;
}
.btn.salir-foco:hover { background: #dc3545; border-color: #dc3545; }
.btn.salir-foco:disabled { opacity: 0.45; cursor: default; }

/* ── Contenedor de la matriz ── */
.matriz-contenedor {
  overflow-x: auto;
  width: 100%;
  cursor: grab;
}
.matriz-contenedor.is-panning { cursor: grabbing; user-select: none; }
.matriz {
  min-width: fit-content;
  display: flex;
  flex-direction: column;
  gap: 0;
}

/* ── Separadores de inserción ── */
.separador-fila {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 26px;
  position: relative;
}
.separador-fila::before {
  content: '';
  position: absolute;
  inset: 50% 0 auto 0;
  height: 1px;
  background: #ebebeb;
}
.btn-insertar {
  position: relative;
  z-index: 1;
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 20px;
  padding: 2px 12px;
  font-size: 11px;
  font-weight: 600;
  color: #c0c0c0;
  cursor: pointer;
  font-family: var(--font-family);
  opacity: 0;
  transition: opacity 0.15s, color 0.15s, border-color 0.15s;
}
.separador-fila:hover .btn-insertar {
  opacity: 1;
  color: #0e69a0;
  border-color: #0e69a0;
}

/* ── Fila de nivel ── */
.nivel-fila {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: white;
  border: 1px solid #e4eaf0;
  border-left: 4px solid #0e69a0;
  border-radius: 8px;
  padding: 10px 14px;
  min-height: 56px;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.05);
  transition: box-shadow 0.15s;
}
.nivel-fila:hover { box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08); }
.nivel-fila.nivel-vacia {
  border-left-color: #bbb;
  border-style: dashed;
  border-left-style: solid;
  background: #fafafa;
  box-shadow: none;
}
.nivel-fila.nivel-vacia:hover { box-shadow: none; }

/* Badge de nivel */
.nivel-badge {
  flex-shrink: 0;
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: #e6f1fb;
  color: #0e69a0;
  font-size: 0.7rem;
  font-weight: 800;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 3px;
  letter-spacing: -0.02em;
}

/* Nivel vacío */
.nivel-vacia-body {
  display: flex;
  align-items: center;
  flex: 1;
  min-height: 36px;
}
.btn-agregar-vacio {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 2px dashed #c4d8e8;
  border-radius: 6px;
  padding: 7px 16px;
  font-size: 0.84rem;
  font-weight: 600;
  color: #90a8bc;
  background: transparent;
  cursor: pointer;
  font-family: var(--font-family);
  transition: border-color 0.15s, color 0.15s;
}
.btn-agregar-vacio:hover { border-color: #0e69a0; color: #0e69a0; }

/* Área de cards */
.nivel-areas {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
}

/* Botón + para agregar en nivel */
.btn-nuevo-area {
  width: 30px;
  height: 30px;
  flex-shrink: 0;
  border: 2px dashed #c4d8e8;
  border-radius: 6px;
  background: transparent;
  font-size: 1rem;
  color: #b0c8da;
  cursor: pointer;
  transition: border-color 0.15s, color 0.15s;
  display: flex;
  align-items: center;
  justify-content: center;
}
.btn-nuevo-area:hover { border-color: #0e69a0; color: #0e69a0; }

/* Panel de detalle (sub-paneles SNIES / Institución / Directivos) */
.panel-detalle {
  background: #f5f9fd;
  border: 1px solid #c8dff0;
  border-left: 4px solid #0e69a0;
  border-radius: 8px;
  padding: 1rem 1.25rem;
  margin: 2px 0;
}

/* ── Botón agregar fila ── */
.btn-agregar-fila {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  border: 2px dashed #ccc;
  border-radius: 8px;
  padding: 13px;
  font-size: 0.875rem;
  font-weight: 600;
  color: #90a0b0;
  background: transparent;
  cursor: pointer;
  font-family: var(--font-family);
  transition: border-color 0.15s, color 0.15s, background 0.15s;
  margin-top: 4px;
  width: 100%;
  box-sizing: border-box;
}
.btn-agregar-fila:hover {
  border-color: #0e69a0;
  color: #0e69a0;
  background: rgba(14, 105, 160, 0.03);
}

/* ── Botones generales ── */
.btn {
  padding: 6px 16px;
  height: 34px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: var(--font-family);
  border: none;
  border-radius: var(--radius-md, 6px);
  cursor: pointer;
  transition: all 0.15s ease;
}
.btn.guardar { background: #eea222; color: #111; }
.btn.guardar:hover { background: #0e69a0; color: white; }
.btn.salir { background: white; border: 1px solid #ccc; color: #333; }
.btn.salir:hover { background: #dc3545; color: white; border-color: #dc3545; }
.btn:disabled { opacity: 0.48; cursor: default; }

/* ══ Modal de reasignación ══ */
.reasignar-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.35);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 999;
}
.reasignar-panel {
  background: white;
  border-radius: 10px;
  padding: 1.5rem;
  width: 420px;
  max-width: 90vw;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.reasignar-panel h3 { margin: 0 0 0.75rem; font-size: 1.1rem; color: #1a1a1a; }
.reasignar-panel p { margin: 0 0 1rem; font-size: 0.9rem; color: #444; line-height: 1.5; }
.reasignar-lista {
  display: flex;
  flex-direction: column;
  gap: 6px;
  margin-bottom: 1.25rem;
  max-height: 220px;
  overflow-y: auto;
}
.reasignar-item {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 0.88rem;
  cursor: pointer;
  padding: 7px 10px;
  border-radius: 6px;
  border: 1px solid #e8e8e8;
  background: #fafafa;
  transition: background 0.12s, border-color 0.12s;
  user-select: none;
}
.reasignar-item:hover { background: #f0f8ff; border-color: #b8d4e8; }
.reasignar-item input[type="checkbox"] {
  width: 15px;
  height: 15px;
  cursor: pointer;
  accent-color: #0e69a0;
  flex-shrink: 0;
}
.reasignar-acciones { display: flex; justify-content: flex-end; gap: 10px; }
</style>
